import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'motion/react';
import { Caption, H1, ButtonPrimary, H2, LabelBold, LabelRegular } from '@comeon/ui';
import { useRouter } from '~/utils/navigation';

import { Modal } from '~/components/modal/modal';
import useTranslations from '~/components/translation/use-translations';
import { getWelcomePageDetails, WelcomePageDetailsResponse } from './utils';
import { showWelcomePopup } from '~/components/authentication/ducks';
import { selectFormattedBalance } from '~/components/account/ducks';
import Icon, { ICON_WAVING_HAND } from '~/components/icon';
import { APIResponseStatuses } from '../../../services/api-new';
import TemporaryAccountLogin from '../../authentication/temporary-account/temp-account-modal-login';
import { selectTempAccountFlow } from '~/components/brand/ducks';
import { isNativeWelcomePopupEnabled } from '~/components/app-bridge/app-properties';
import { ROUTE_RESPONSIBLEGAMING } from '../../router/constants';
import Anchor from '../../misc/anchor';

const WelcomePopup = styled.div`
	background: var(--welcome-popup-bg);
	border-top-left-radius: 25px;
	border-top-right-radius: 25px;
	padding: 24px;
`;

const Header = styled.div`
	padding: 9px 0;
	svg {
		fill: var(--player-info-cgr-icon);
		margin: 0 auto;
	}
`;

const StyledH2 = styled(H2)`
	margin-top: 7px;
`;

const StyledCaption = styled(Caption)`
	margin-top: 7px;
`;

const BalanceContainer = styled.div`
	background-color: var(--player-info-cgr-bg);
	padding: 12px;
	margin-top: 20px;
	border-radius: 12px;
`;

const CumulativeGRContainer = styled.div`
	margin-top: 24px;
`;

const CumulativeGRRow = styled.div`
	padding: 12px 0;
	border-bottom: 1px solid var(--player-info-balance-border);
`;

const LabelRegularWrapper = styled(LabelRegular)`
	color: var(--player-info-balance-desc);
`;

const RGInfo = styled.p`
	margin-top: 24px;
	font: var(--caption);
`;

const RGInfoLink = styled.span`
	text-decoration: underline;
	color: rgba(var(--font-brand), 1);
`;

const StyledButton = styled(ButtonPrimary)`
	margin-top: 24px;
`;

const variants = {
	sidebarOpen: {
		opacity: 1,
		y: 0
	},
	sidebarClosed: {
		y: 600,
		opacity: 0
	},
	exit: {
		y: 600,
		opacity: 0
	}
};

const transition = {
	duration: 0.2
};

const textKeys = [
	'WS_HERO_TITLE',
	'WS_HERO_DESC',
	'BALANCE',
	'WS_GAME_RESULTS_TITLE',
	'WS_GAME_RESULTS_DESC',
	'WS_LOST_WON',
	'WS_YOU_WAGERED',
	'GERMAN_PLAYER_LOGIN_GAMBLING_ADDICTIVE',
	'GERMAN_PLAYER_LOGIN_RESPONSIBLE_GAMING_PAGE',
	'MOBILE_METHOD_CONTINUE'
];

const WelcomeScreenPopUpDEGA = () => {
	const [playerInfo, setPlayerInfo] = useState<WelcomePageDetailsResponse>();
	const formattedBalance = useSelector(selectFormattedBalance);
	const showTempAccountFlow = useSelector(selectTempAccountFlow) as boolean;
	const { translations, isLoadingTranslations } = useTranslations(textKeys);
	const dispatch = useDispatch();
	const router = useRouter();
	// we need this state for animation purposes
	const [isWelcomeScreenVisible, setShowWelcomeScreen] = useState(false);
	const isRenderedByApp = isNativeWelcomePopupEnabled();

	useEffect(() => {
		const hydratePlayerInfo = async () => {
			const response = await getWelcomePageDetails();
			if (response.status === APIResponseStatuses.SUCCESS) {
				setPlayerInfo(response.result);
				setShowWelcomeScreen(true);
			}
		};

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		hydratePlayerInfo();
	}, []);

	const handleContinue = useCallback(() => {
		setShowWelcomeScreen(false);
		// we need to hide popup with delay to show the animation
		setTimeout(() => {
			dispatch(showWelcomePopup(false));
		}, 200);
	}, [dispatch]);

	const handleRGClick = useCallback(() => {
		dispatch(showWelcomePopup(false));

		router.push(ROUTE_RESPONSIBLEGAMING);
	}, [dispatch, router]);

	return (
		<>
			{!isRenderedByApp && (
				<Modal show welcomePopup modalWidth={375} blurBg>
					<AnimatePresence mode="wait">
						{isWelcomeScreenVisible && !isLoadingTranslations && (
							<motion.div
								variants={variants}
								initial="sidebarClosed"
								animate="sidebarOpen"
								exit="exit"
								key="slideUp"
								transition={transition}
							>
								<WelcomePopup>
									<Header className="u-text-c">
										<Icon width={56} height={56} type={ICON_WAVING_HAND} />
										<StyledH2 data-at="main-title_welcomepage">
											{translations.WS_HERO_TITLE}
										</StyledH2>
										<StyledCaption data-at="sub-title_welcompage">
											{translations.WS_HERO_DESC}
										</StyledCaption>
									</Header>
									<BalanceContainer>
										<H1 data-at="balance_welcomepage">
											{formattedBalance || playerInfo?.totalBalance || (
												<div className="spinner-mini" />
											)}
										</H1>
										<Caption>{translations.BALANCE}</Caption>
									</BalanceContainer>
									<CumulativeGRContainer data-at="cumulative-game-results">
										<LabelBold>{translations.WS_GAME_RESULTS_TITLE}</LabelBold>
										<Caption>{translations.WS_GAME_RESULTS_DESC}</Caption>
										<CumulativeGRRow className="u-flex u-space-between">
											<LabelBold>{translations.WS_LOST_WON}</LabelBold>
											<LabelRegularWrapper>
												{playerInfo?.accumulatedLoss ?? 0}
											</LabelRegularWrapper>
										</CumulativeGRRow>
										<CumulativeGRRow className="u-flex u-space-between">
											<LabelBold>{translations.WS_YOU_WAGERED}</LabelBold>
											<LabelRegularWrapper>
												{playerInfo?.accumulatedWager ?? 0}
											</LabelRegularWrapper>
										</CumulativeGRRow>
									</CumulativeGRContainer>
									<RGInfo>
										<span>{translations.GERMAN_PLAYER_LOGIN_GAMBLING_ADDICTIVE}</span>
										<Anchor onClick={handleRGClick}>
											<RGInfoLink>
												{translations.GERMAN_PLAYER_LOGIN_RESPONSIBLE_GAMING_PAGE}
											</RGInfoLink>
										</Anchor>
									</RGInfo>
									<StyledButton dataAt="continue-welcomepage" onClick={handleContinue}>
										{translations.MOBILE_METHOD_CONTINUE}
									</StyledButton>
								</WelcomePopup>
							</motion.div>
						)}
					</AnimatePresence>
				</Modal>
			)}
			{showTempAccountFlow && <TemporaryAccountLogin expired={false} />}
		</>
	);
};

export default WelcomeScreenPopUpDEGA;
